import {Component, OnInit, ViewChild} from '@angular/core';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexNonAxisChartSeries,
    ApexResponsive,
    ApexStroke,
    ApexTitleSubtitle, ApexTooltip,
    ApexXAxis,
    ApexYAxis
} from 'ng-apexcharts';
import {ReportsCrmService} from '../_services/reports-crm.service';
import {AngularMyDatePickerDirective, IAngularMyDpOptions} from 'angular-mydatepicker';
import {FormControl} from '@angular/forms';
import {formatDate} from '@angular/common';

export type LineChartOptions = {
    series: ApexAxisChartSeries;
    stroke: ApexStroke;
    chart: ApexChart;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    title: ApexTitleSubtitle;
    grid: ApexGrid;
    dataLabels: ApexDataLabels;
    tooltip: ApexTooltip;
};

export type PieChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    labels: any;
};

export type AreaChartStatus = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    xaxis: ApexXAxis;
    stroke: ApexStroke;
    dataLabels: ApexDataLabels;
    yaxis: ApexYAxis;
    labels: any;
    grid: ApexGrid;
    legend: ApexLegend;
    tooltip: ApexTooltip;
};

type xAxis = 'category' | 'datetime';

@Component({
    selector: 'app-tickets-reports',
    templateUrl: './tickets-reports.component.html',
    styleUrls: ['./tickets-reports.component.scss']
})
export class TicketsReportsComponent implements OnInit {

    // Loaded Data
    lineChartLoaded: boolean;
    pieLoaded: boolean;
    statusNewLoaded: boolean;
    statusInProgressLoaded: boolean;
    statusDoneLoaded: boolean;
    statusCancelledLoaded: boolean;

    // Generals
    ticketStatus = ['New', 'In progress', 'Done', 'Cancelled'];

    // Apex Charts Options
    lineChartOptions: Partial<LineChartOptions>;
    pieChartOptions: Partial<PieChartOptions>;
    areaChartStatusNew: Partial<AreaChartStatus>;
    areaChartStatusInProgress: Partial<AreaChartStatus>;
    areaChartStatusDone: Partial<AreaChartStatus>;
    areaChartStatusCancelled: Partial<AreaChartStatus>;

    // Form Control
    myDateRange: any = new FormControl({
        isRange: true,
        dateRange: {
            beginJsDate: new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1),
            endJsDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
        }
    });
    frequencySelect = new FormControl('daily');


    // Datepicker
    @ViewChild(AngularMyDatePickerDirective) datePicker: AngularMyDatePickerDirective;
    myOptions: IAngularMyDpOptions = {
        dateRange: true,
        dateFormat: 'yyyy-mm-dd',
        markCurrentDay: true,
        dateRangeDatesDelimiter: '  --  ',
        calendarAnimation: {in: 1, out: 1},
    };

    startDate = formatDate(this.myDateRange.value.dateRange.beginJsDate, 'yyyy-MM-dd', 'en');
    endDate = formatDate(this.myDateRange.value.dateRange.endJsDate, 'yyyy-MM-dd', 'en');

    // Pie Charts
    pieSeries: any;
    pieLabels: any;

    // Total Numbers
    totalNewTickets = 0;
    totalInProgressTickets = 0;
    totalDoneTickets = 0;
    totalCancelledTickets = 0;

    constructor(private reportsCrmService: ReportsCrmService) {

    }

    ngOnInit(): void {

        // Main Chart
        this.getCreatedTickets(this.startDate, this.endDate, this.frequencySelect.value);

        // Pie Chart
        this.getPieData(this.startDate, this.endDate, this.ticketStatus);

        // Initiate Data by Status for Area Chart
        this.getNewTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getInProgressTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getDoneTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getCancelledTickets(this.startDate, this.endDate, this.frequencySelect.value);
    }


    // Main Chart
    getCreatedTickets(startDate, endDate, frequency) {

        let xAxisType;
        if (frequency === 'daily') {
            xAxisType = 'datetime';
        } else {
            xAxisType = 'category';
        }

        this.reportsCrmService.getCreatedTickets(startDate, endDate, frequency).subscribe((createdTickets: any) => {
            const myData = [];
            const myCateg = [];
            const intervalLabel = [];

            createdTickets.forEach(data => {
                myData.push(data.number);
                myCateg.push(data.time);
                intervalLabel.push(data.interval);
            });

            this.lineChartOptions = {
                series: [
                    {
                        name: 'Created Tickets',
                        data: myData
                    }
                ],
                stroke: {
                    curve: 'smooth'
                },
                dataLabels: {
                    enabled: false
                },
                chart: {
                    height: 350,
                    type: 'line',
                },
                grid: {
                    borderColor: '#f3f3f3',
                    row: {
                        colors: ['#f3f3f3', 'transparent'],
                        opacity: 0.5
                    }
                },
                tooltip: {
                    x: {
                        show: true,
                        formatter: (val: any, series) => {
                            if (frequency === 'weekly') {
                                return intervalLabel[series.dataPointIndex];
                            } else {
                                return myCateg[series.dataPointIndex];
                            }
                        },
                    },
                },
                xaxis: {
                    categories: myCateg,
                    type: xAxisType
                },
                title: {
                    text: 'Created Tickets'
                },
                yaxis: {

                    labels: {
                        formatter: (value: any) => {
                            return value;
                        },
                    },
                },
            };

            this.lineChartLoaded = true;

        });
    }

    // Pie Chart Data
    getPieData(start, end, statusList) {

        this.pieLabels = [];
        this.pieSeries = [];
        this.pieLoaded = false;

        let itemsProcessed = 0;

        statusList.forEach(statusName => {
            this.reportsCrmService.getPieChartData(start, end, statusName).subscribe((data: any) => {
                this.pieLabels.push(statusName);
                this.pieSeries.push(data.number);

                itemsProcessed++;
                if (itemsProcessed === statusList.length) {
                    this.pieChartOptions = {
                        series: this.pieSeries,
                        chart: {
                            width: 380,
                            type: 'pie'
                        },
                        labels: this.pieLabels,
                        responsive: [
                            {
                                breakpoint: 480,
                                options: {
                                    chart: {
                                        width: 200
                                    },
                                    legend: {
                                        position: 'bottom'
                                    }
                                }
                            }
                        ]
                    };
                    this.pieLoaded = true;
                }
            });
        });
    }

    // Area Chart - Tickets by Status
    getNewTickets(start, end, frequency) {

        let xAxisType;
        if (frequency === 'daily') {
            xAxisType = 'datetime';
        } else {
            xAxisType = 'category';
        }

        this.reportsCrmService.getTicketsByStatus(start, end, frequency, 'New').subscribe((newTickets: any) => {

            const myData = [];
            const myCateg = [];
            const intervalLabel = [];
            this.totalNewTickets = 0;

            newTickets.forEach(data => {
                myData.push(data.number);
                myCateg.push(data.time);
                intervalLabel.push(data.interval);

                this.totalNewTickets += data.number;
            });


            this.areaChartStatusNew = {
                series: [
                    {
                        name: 'Status New',
                        data: myData
                    }
                ],
                chart: {
                    type: 'area',
                    height: 150,
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: true
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth'
                },
                tooltip: {
                    x: {
                        show: true,
                        formatter: (val: any, series) => {
                            if (frequency === 'weekly') {
                                return intervalLabel[series.dataPointIndex];
                            } else {
                                return myCateg[series.dataPointIndex];
                            }
                        },
                    },
                },
                labels: myCateg,
                xaxis: {
                    type: xAxisType,
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: false,
                    },
                    labels: {
                        show: false,
                    }
                },
                grid: {
                    show: false
                },
                yaxis: {
                    show: false,
                    labels: {
                        formatter: (value: any) => {
                            return value;
                        },
                    },
                },
                legend: {
                    horizontalAlign: 'left'
                }
            };

            this.statusNewLoaded = true;
        });
    }

    getInProgressTickets(start, end, frequency) {

        let xAxisType;
        if (frequency === 'daily') {
            xAxisType = 'datetime';
        } else {
            xAxisType = 'category';
        }

        this.reportsCrmService.getTicketsByStatus(start, end, frequency, 'In progress').subscribe((inProgressTickets: any) => {
            const myData = [];
            const myCateg = [];
            const intervalLabel = [];
            this.totalInProgressTickets = 0;

            inProgressTickets.forEach(data => {
                myData.push(data.number);
                myCateg.push(data.time);
                intervalLabel.push(data.interval);

                this.totalInProgressTickets += data.number;
            });


            this.areaChartStatusInProgress = {
                series: [
                    {
                        name: 'Status New',
                        data: myData
                    }
                ],
                chart: {
                    type: 'area',
                    height: 150,
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: true
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth'
                },
                tooltip: {
                    x: {
                        show: true,
                        formatter: (val: any, series) => {
                            if (frequency === 'weekly') {
                                return intervalLabel[series.dataPointIndex];
                            } else {
                                return myCateg[series.dataPointIndex];
                            }
                        },
                    },
                },
                labels: myCateg,
                xaxis: {
                    type: xAxisType,
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: false,
                    },
                    labels: {
                        show: false,
                    }
                },
                grid: {
                    show: false
                },
                yaxis: {
                    show: false,
                    labels: {
                        formatter: (value: any) => {
                            return value;
                        },
                    },
                },
                legend: {
                    horizontalAlign: 'left'
                }
            };

            this.statusInProgressLoaded = true;
        });
    }

    getDoneTickets(start, end, frequency) {

        let xAxisType;
        if (frequency === 'daily') {
            xAxisType = 'datetime';
        } else {
            xAxisType = 'category';
        }

        this.reportsCrmService.getTicketsByStatus(start, end, frequency, 'Done').subscribe((doneTickets: any) => {

            const myData = [];
            const myCateg = [];
            const intervalLabel = [];
            this.totalDoneTickets = 0;

            doneTickets.forEach(data => {
                myData.push(data.number);
                myCateg.push(data.time);
                intervalLabel.push(data.interval);

                this.totalDoneTickets += data.number;
            });


            this.areaChartStatusDone = {
                series: [
                    {
                        name: 'Status New',
                        data: myData
                    }
                ],
                chart: {
                    type: 'area',
                    height: 150,
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: true
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth'
                },
                tooltip: {
                    x: {
                        show: true,
                        formatter: (val: any, series) => {
                            if (frequency === 'weekly') {
                                return intervalLabel[series.dataPointIndex];
                            } else {
                                return myCateg[series.dataPointIndex];
                            }
                        },
                    },
                },
                labels: myCateg,
                xaxis: {
                    type: xAxisType,
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: false,
                    },
                    labels: {
                        show: false,
                    }
                },
                grid: {
                    show: false
                },
                yaxis: {
                    show: false,
                    labels: {
                        formatter: (value: any) => {
                            return value;
                        },
                    },
                },
                legend: {
                    horizontalAlign: 'left'
                }
            };

            this.statusDoneLoaded = true;
        });
    }

    getCancelledTickets(start, end, frequency) {

        let xAxisType;
        if (frequency === 'daily') {
            xAxisType = 'datetime';
        } else {
            xAxisType = 'category';
        }

        this.reportsCrmService.getTicketsByStatus(start, end, frequency, 'Cancelled').subscribe((cancelledTickets: any) => {


            const myData = [];
            const myCateg = [];
            const intervalLabel = [];
            this.totalCancelledTickets = 0;

            cancelledTickets.forEach(data => {
                myData.push(data.number);
                myCateg.push(data.time);
                intervalLabel.push(data.interval);

                this.totalCancelledTickets += data.number;
            });


            this.areaChartStatusCancelled = {
                series: [
                    {
                        name: 'Status New',
                        data: myData
                    }
                ],
                chart: {
                    type: 'area',
                    height: 150,
                    toolbar: {
                        show: false
                    },
                    zoom: {
                        enabled: true
                    }
                },
                dataLabels: {
                    enabled: false
                },
                stroke: {
                    curve: 'smooth'
                },
                tooltip: {
                    x: {
                        show: true,
                        formatter: (val: any, series) => {
                            if (frequency === 'weekly') {
                                return intervalLabel[series.dataPointIndex];
                            } else {
                                return myCateg[series.dataPointIndex];
                            }
                        },
                    },
                },
                labels: myCateg,
                xaxis: {
                    type: xAxisType,
                    axisBorder: {
                        show: false,
                    },
                    axisTicks: {
                        show: false,
                    },
                    labels: {
                        show: false,
                    }
                },
                grid: {
                    show: false
                },
                yaxis: {
                    show: false,
                    labels: {
                        formatter: (value: any) => {
                            return value;
                        },
                    },
                },
                legend: {
                    horizontalAlign: 'left'
                }
            };

            this.statusCancelledLoaded = true;
        });
    }


    changeDataRange(event) {
        this.startDate = formatDate(event.dateRange.beginJsDate, 'yyyy-MM-dd', 'en');
        this.endDate = formatDate(event.dateRange.endJsDate, 'yyyy-MM-dd', 'en');
        this.getCreatedTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getPieData(this.startDate, this.endDate, this.ticketStatus);

        // Get tickets by status
        this.getNewTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getInProgressTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getDoneTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getCancelledTickets(this.startDate, this.endDate, this.frequencySelect.value);
    }

    changeDataFrequency() {
        this.getCreatedTickets(this.startDate, this.endDate, this.frequencySelect.value);

        // Get tickets by status
        this.getNewTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getInProgressTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getDoneTickets(this.startDate, this.endDate, this.frequencySelect.value);
        this.getCancelledTickets(this.startDate, this.endDate, this.frequencySelect.value);
    }
}
